// UserPanelContent.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AvatarModal from './AvatarModal';
import ObjectEditModal from './ObjectEditModal';
import UserDataModal from './UserDataModal';
import DocumentosModal from './DocumentosModal';
import FotoIDModal from './FotoIDModal';
import DashboardBusqueda from './DashboardBusqueda';
import ModalOverlay from "../../components/ModalOverlay";
import DynamicForm from '../../components/forms/DynamicForm';
import ExpedienteClinicoVeterinarioList from '../components/ExpedienteClinicoVeterinarioList';
import { isPlanVigente, getDiasRestantes, getFechaVencimientoFormateada, tieneAccesoA } from '../../utils/planUtils';
import { API_URL } from '../../services/api/config';

// ReminderManager eliminado - ahora se usa QuickReminder desde RecordCard
import QuickReminder from './QuickReminder';
import ProvidersList from './ProvidersList';

// Función helper para obtener el nombre correcto según el tipo
const getNombreSegunTipo = (obj) => {
  if (obj.tipo === 'mascota') {
    return obj.nombre || 'Mascota sin nombre';
  } else {
    return obj.nombreObjeto || obj.nombre || `${obj.tipo || 'Objeto'} sin nombre`;
  }
};

/**
 * Genera la URL de foto usando el proxy endpoint (evita 403 de Storage)
 * @param {string} objetoId - ID del objeto
 * @param {string} fotoUrl - URL original de la foto (para verificar si existe)
 * @param {string|Date|Object} updatedAt - Timestamp para cache-busting
 * @returns {string|null} URL del proxy o null si no hay foto
 */
const getFotoProxyUrl = (objetoId, fotoUrl, updatedAt) => {
  if (!objetoId || !fotoUrl) return null;

  // Cache-busting: usar updatedAt para forzar recarga cuando cambia la foto
  let cacheBuster = '';
  if (updatedAt) {
    const timestamp = updatedAt?.toMillis?.() ||
                     updatedAt?.seconds * 1000 ||
                     (typeof updatedAt === 'string' ? new Date(updatedAt).getTime() : null) ||
                     Date.now();
    cacheBuster = `?v=${timestamp}`;
  }

  return `${API_URL}/api/archivo/objeto/${objetoId}/foto${cacheBuster}`;
};

/**
 * Calcula el porcentaje de completitud del perfil de una mascota
 */
const calcularProgresoPerfil = (mascota) => {
  let progreso = 0;

  // Perfil básico (40%): nombre, especie, foto principal
  const tieneBasico = mascota.nombre && mascota.especie && mascota.fotoUrl;
  if (tieneBasico) progreso += 40;

  // Perfil extendido (30%): al menos 3 de 5 campos opcionales
  const camposExtendidos = [
    mascota.raza,
    mascota.edad || mascota.fechaNacimiento,
    mascota.esterilizado !== undefined && mascota.esterilizado !== '',
    mascota.alergias,
    mascota.comportamientoConExtranos
  ].filter(Boolean).length;
  progreso += Math.round((camposExtendidos / 5) * 30);

  // Foto-ID (30%)
  if (mascota.fotoID?.completo === true) progreso += 30;

  return Math.min(100, progreso);
};

export function ObjectCard({ objeto, onEditar, onReportar, onBloquear, onVerExpediente, onVerCartilla, onShowCitas }) {
  const { tipo = 'objeto', plan, extraviado, fotoUrl, descripcion, bloqueado, enTratamiento, tratamientoActivoId } = objeto;
  const nombre = getNombreSegunTipo(objeto);
  const navigate = useNavigate();

  // URL de foto usando proxy (evita 403 de Storage directo)
  // ✅ Incluir updatedAt para cache-busting cuando cambia la foto
  const fotoProxyUrl = getFotoProxyUrl(objeto.id, fotoUrl, objeto.updatedAt);

  // Tratamiento activo (solo mascotas)
  const tieneTratamientoActivo = tipo === 'mascota' && enTratamiento && tratamientoActivoId;

  // Profile completion (solo mascotas)
  const progresoPerfil = tipo === 'mascota' ? calcularProgresoPerfil(objeto) : null;

  // ✅ USAR FUNCIONES HELPER
  const planVigente = isPlanVigente(objeto);
  const diasRestantes = getDiasRestantes(objeto);
  const fechaVencimiento = getFechaVencimientoFormateada(objeto);
  const mostrarAvisoRenovacion = diasRestantes !== null && diasRestantes <= 30 && diasRestantes > 0;

  // Estado para el modal de imagen
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Estado para modal de documentos (solo objetos Premium, NO mascotas)
  const [showDocumentosModal, setShowDocumentosModal] = useState(false);
  const puedeVerDocumentos = plan === 'premium' && tipo !== 'mascota' && planVigente;

  // Estado para dropdown menu, Foto-ID, Dashboard de Búsqueda y Recordatorios
  const [showFotoIDModal, setShowFotoIDModal] = useState(false);
  const [showDashboardBusqueda, setShowDashboardBusqueda] = useState(false);
  const [showQuickReminder, setShowQuickReminder] = useState(false);
  const [fotoIDCompleto, setFotoIDCompleto] = useState(!!objeto.fotoID?.completo);

  // Recordatorios disponibles para Standard/Premium (mascotas) y Premium (objetos)
  const puedeCrearRecordatorios = planVigente && (
    (tipo === 'mascota' && (plan === 'basico' || plan === 'premium')) ||
    (tipo !== 'mascota' && plan === 'premium')
  );
  const dropdownRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [dropdownOpen]);

  // Emoji placeholder según el tipo de objeto
  const getPlaceholderEmoji = () => {
    switch (tipo) {
      case 'mascota': return '🐾';
      case 'llavero': return '🔑';
      case 'coleccionable': return '📦';
      default: return '🏷️';
    }
  };

  return (
    <div className="col-lg-6 mb-4">
      <div className="card h-100 card-elevated object-card" style={{ position: 'relative' }}>
        {/* Dropdown menu (⋮) - siempre visible */}
        <div ref={dropdownRef} style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 2 }}>
          <button
            className="btn btn-link text-muted p-1"
            onClick={() => setDropdownOpen(!dropdownOpen)}
            style={{
              fontSize: '1.3rem',
              lineHeight: 1,
              textDecoration: 'none',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Opciones"
          >
            <i className="bi bi-three-dots-vertical"></i>
          </button>
          {dropdownOpen && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              backgroundColor: 'var(--white)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--gray-200)',
              boxShadow: 'var(--shadow-xl)',
              minWidth: '180px',
              padding: '4px 0',
              zIndex: 50
            }}>
              {/* Foto-ID - solo mascotas */}
              {tipo === 'mascota' && (
                <button
                  className="dropdown-item d-flex align-items-center"
                  style={{
                    width: '100%',
                    border: 'none',
                    background: 'none',
                    padding: '8px 16px',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--gray-800)',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                  onClick={() => {
                    setDropdownOpen(false);
                    setShowFotoIDModal(true);
                  }}
                >
                  <span style={{ marginRight: '8px' }}>📸</span>
                  Foto-ID
                  {fotoIDCompleto && (
                    <span style={{ marginLeft: '6px', color: 'var(--feature-primary)' }}>✓</span>
                  )}
                </button>
              )}
              {/* Dashboard de búsqueda - solo mascotas extraviadas */}
              {tipo === 'mascota' && extraviado && (
                <button
                  className="dropdown-item d-flex align-items-center"
                  style={{
                    width: '100%',
                    border: 'none',
                    background: 'none',
                    padding: '8px 16px',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--gray-800)',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                  onClick={() => {
                    setDropdownOpen(false);
                    setShowDashboardBusqueda(true);
                  }}
                >
                  <i className="bi bi-search me-2" style={{ fontSize: 'var(--font-size-sm)' }}></i>
                  Dashboard de búsqueda
                </button>
              )}
              {/* Recordatorios - Standard/Premium mascotas, Premium objetos */}
              {puedeCrearRecordatorios && (
                <button
                  className="dropdown-item d-flex align-items-center"
                  style={{
                    width: '100%',
                    border: 'none',
                    background: 'none',
                    padding: '8px 16px',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--gray-800)',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                  onClick={() => {
                    setDropdownOpen(false);
                    setShowQuickReminder(true);
                  }}
                >
                  <span style={{ marginRight: '8px' }}>🔔</span>
                  Recordatorios
                </button>
              )}

              {/* Expediente - en menú de 3 puntos para mascotas Standard/Premium */}
              {tipo === 'mascota' && tieneAccesoA(objeto, 'expediente') && (
                <button
                  className="dropdown-item d-flex align-items-center"
                  style={{
                    width: '100%',
                    border: 'none',
                    background: 'none',
                    padding: '8px 16px',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--gray-800)',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                  onClick={() => {
                    setDropdownOpen(false);
                    onVerExpediente(objeto);
                  }}
                >
                  <i className="bi bi-file-medical me-2" style={{ fontSize: 'var(--font-size-sm)' }}></i>
                  Expediente
                </button>
              )}

              {/* Separador visual */}
              {(tipo === 'mascota' || puedeCrearRecordatorios) && (
                <div style={{
                  height: '1px',
                  backgroundColor: 'var(--gray-200)',
                  margin: '4px 0'
                }}></div>
              )}

              {/* Editar */}
              <button
                className="dropdown-item d-flex align-items-center"
                style={{
                  width: '100%',
                  border: 'none',
                  background: 'none',
                  padding: '8px 16px',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--gray-800)',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
                onClick={() => {
                  setDropdownOpen(false);
                  onEditar(objeto);
                }}
              >
                <i className="bi bi-pencil-square me-2" style={{ fontSize: 'var(--font-size-sm)' }}></i>
                Editar
              </button>

              {/* Bloquear/Desbloquear */}
              {planVigente && (
                <button
                  className="dropdown-item d-flex align-items-center"
                  style={{
                    width: '100%',
                    border: 'none',
                    background: 'none',
                    padding: '8px 16px',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--gray-800)',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                  onClick={() => {
                    setDropdownOpen(false);
                    onBloquear(objeto);
                  }}
                >
                  <i className={`bi ${bloqueado ? "bi-unlock" : "bi-lock"} me-2`} style={{ fontSize: 'var(--font-size-sm)' }}></i>
                  {bloqueado ? 'Desbloquear' : 'Bloquear'}
                </button>
              )}

              {/* Servicios (Localizador) */}
              {planVigente && (
                <button
                  className="dropdown-item d-flex align-items-center"
                  style={{
                    width: '100%',
                    border: 'none',
                    background: 'none',
                    padding: '8px 16px',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--gray-800)',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                  onClick={() => {
                    setDropdownOpen(false);
                    navigate(
                      tipo === 'mascota'
                        ? `/localizador/mascota/${objeto.id}`
                        : `/localizador/objeto/${objeto.id}`
                    );
                  }}
                >
                  <i className="bi bi-search me-2" style={{ fontSize: 'var(--font-size-sm)' }}></i>
                  Buscar servicios
                </button>
              )}
            </div>
          )}
        </div>

        <div className="card-body d-flex flex-column">
          <div className="d-flex align-items-center mb-3">
            {/* Contenedor mejorado para la imagen */}
            <div
              style={{
                width: '80px',
                height: '80px',
                overflow: 'hidden',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--gray-100)',
                flexShrink: 0,
                cursor: fotoProxyUrl && !imageError ? 'pointer' : 'default',
                marginRight: 'var(--space-4)',
                transition: 'var(--transition-fast)'
              }}
              onClick={() => fotoProxyUrl && !imageError && setShowImageModal(true)}
              title={fotoProxyUrl && !imageError ? 'Click para ampliar' : ''}
            >
              {fotoProxyUrl && !imageError ? (
                <img
                  src={fotoProxyUrl}
                  alt={`Imagen de ${nombre}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  onError={() => setImageError(true)}
                />
              ) : (
                <div style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '40px',
                  backgroundColor: '#f0f0f0'
                }}>
                  {getPlaceholderEmoji()}
                </div>
              )}
            </div>
            
            <div className="flex-grow-1">
              <h5 className="card-title mb-1 font-bold"
                  style={{
                    fontSize: 'var(--font-size-xl)',
                    color: 'var(--gray-900)'
                  }}>
                {nombre}
              </h5>
              {/* Badge de estado */}
              {extraviado && (
              <div className="mb-2">
                  <span className="badge bg-danger rounded-pill">
                    <i className="bi bi-exclamation-triangle-fill me-1"></i>Perdido
                  </span>
              </div>
              )}
              {tipo !== 'mascota' && descripcion && (
                <p className="card-text text-muted mb-2" 
                   style={{
                     fontSize: 'var(--font-size-sm)',
                     lineHeight: 'var(--line-height-normal)'
                   }}>
                  {descripcion}
                </p>
              )}
              
              {/* ✅ NUEVA SECCIÓN: Mostrar fecha de vencimiento siempre */}
              {fechaVencimiento && (
                <div className={`mb-2 ${!planVigente ? 'text-danger' : 'text-muted'}`}
                     style={{
                       fontSize: 'var(--font-size-sm)',
                       fontWeight: 'var(--font-weight-medium)'
                     }}>
                  <i className={`bi ${planVigente ? 'bi-calendar-check' : 'bi-calendar-x'} me-1`}
                     style={{fontSize: 'var(--font-size-xs)'}}></i>
                  <strong style={{fontWeight: 'var(--font-weight-semibold)'}}>
                    Plan {planVigente ? 'activo hasta' : 'venció el'}:
                  </strong> {fechaVencimiento}
                  {diasRestantes !== null && planVigente && diasRestantes <= 30 && (
                    <span className="text-warning ms-2" style={{fontSize: 'var(--font-size-xs)'}}>
                      ({diasRestantes} {diasRestantes === 1 ? 'día' : 'días'} restantes)
                    </span>
                  )}
                </div>
              )}
              
              {mostrarAvisoRenovacion && (
                <div className="alert alert-warning py-2 px-3 mb-2 rounded-lg"
                     style={{fontSize: 'var(--font-size-xs)'}}>
                  <i className="bi bi-exclamation-triangle me-1"></i>
                  Tu plan vence en {diasRestantes} días
                  {' - '}
                  <a href="https://lemmontags.com/renovar"
                     target="_blank"
                     rel="noopener noreferrer"
                     className="text-decoration-underline fw-semibold">
                    Renovar ahora
                  </a>
                </div>
              )}
              
              {!planVigente && (
                <div className="alert alert-danger py-2 px-3 mb-2 rounded-lg"
                     style={{fontSize: 'var(--font-size-xs)'}}>
                  <i className="bi bi-x-circle me-1"></i>
                  Plan expirado -
                  <a href="https://lemmontags.com/renovar"
                     target="_blank"
                     rel="noopener noreferrer"
                     className="text-decoration-underline fw-semibold">
                    {" "}Renovar ahora
                  </a>
                </div>
              )}
              
              {bloqueado && (
                <div className="alert alert-info py-2 px-3 mb-2 rounded-lg"
                     style={{fontSize: 'var(--font-size-xs)'}}>
                  <i className="bi bi-lock-fill me-1"></i>La llave de objeto está bloqueada
                </div>
              )}

              {/* Badge de tratamiento activo (solo mascotas) */}
              {tieneTratamientoActivo && (
                <div
                  onClick={() => navigate(`/tratamientos/${tratamientoActivoId}`)}
                  style={{
                    backgroundColor: '#dcfce7',
                    border: '1px solid #bbf7d0',
                    borderRadius: '6px',
                    padding: '4px 8px',
                    marginBottom: '8px',
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  <span style={{ fontWeight: 500, color: '#166534', fontSize: '0.75rem' }}>
                    💊 En tratamiento
                  </span>
                  <span style={{ color: '#166534', fontWeight: 500, fontSize: '0.7rem' }}>
                    Ver progreso →
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="mt-auto pt-3 border-top"
               style={{borderTopColor: 'var(--gray-300)'}}>
            <div className="action-buttons d-flex flex-wrap"
                 style={{gap: 'var(--space-2)'}}>
              {/* Marcar perdido/encontrado - accion urgente, siempre visible */}
              {planVigente && (
                <button className={`btn btn-sm ${extraviado ? 'btn-outline-success' : 'btn-outline-danger'} transition-base`}
                        onClick={() => onReportar(objeto)}>
                  {extraviado
                    ? <><i className="bi bi-check-circle me-1"></i>Marcar encontrado</>
                    : <><i className="bi bi-exclamation-triangle me-1"></i>Marcar perdido</>}
                </button>
              )}

              {/* Ver Cartilla: disponible para Standard y Premium mascotas */}
              {tipo === 'mascota' && tieneAccesoA(objeto, 'expediente') && (
                <button className="btn btn-sm btn-outline-success transition-base"
                        onClick={() => onVerCartilla(objeto)}>
                  <i className="bi bi-journal-medical me-1"></i>Ver Cartilla
                </button>
              )}

              {/* Citas: solo Premium mascotas */}
              {tipo === 'mascota' && tieneAccesoA(objeto, 'citas') && (
                <button className="btn btn-sm btn-outline-primary transition-base"
                        onClick={() => onShowCitas(objeto)}>
                  <i className="bi bi-calendar-check me-1"></i>Citas
                </button>
              )}

              {/* Documentos - Solo objetos Premium (no mascotas) */}
              {puedeVerDocumentos && (
                <button className="btn btn-sm btn-outline-info transition-base"
                        onClick={() => setShowDocumentosModal(true)}>
                  <i className="bi bi-folder2-open me-1"></i>Documentos
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Documentos (solo objetos Premium no mascota) */}
      {puedeVerDocumentos && (
        <DocumentosModal
          show={showDocumentosModal}
          onClose={() => setShowDocumentosModal(false)}
          objetoId={objeto.id}
          objetoNombre={nombre}
        />
      )}
      
      {/* Modal de imagen ampliada */}
      {showImageModal && (
        <div 
          className="modal fade show d-block" 
          style={{ 
            backgroundColor: "rgba(0,0,0,0.8)",
            zIndex: 9999
          }}
          onClick={() => setShowImageModal(false)}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content bg-transparent border-0">
              <div className="modal-body p-0 text-center">
                <img
                  src={fotoProxyUrl}
                  alt={`Imagen ampliada de ${nombre}`}
                  style={{ 
                    maxWidth: '100%',
                    maxHeight: '80vh',
                    objectFit: 'contain',
                    borderRadius: 'var(--radius-lg)'
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
                <button
                  className="btn btn-light mt-3"
                  onClick={() => setShowImageModal(false)}
                >
                  <i className="bi bi-x-lg me-2"></i>Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Foto-ID (solo mascotas) */}
      {showFotoIDModal && tipo === 'mascota' && (
        <FotoIDModal
          mascota={objeto}
          onClose={() => setShowFotoIDModal(false)}
          onFotoIDUpdated={(fotoID) => setFotoIDCompleto(!!fotoID?.completo)}
        />
      )}

      {/* Modal Dashboard de Búsqueda (solo mascotas extraviadas) */}
      {showDashboardBusqueda && tipo === 'mascota' && extraviado && (
        <DashboardBusqueda
          mascota={objeto}
          onClose={() => setShowDashboardBusqueda(false)}
          onEditarPerdida={(obj) => onEditar(obj)}
        />
      )}

      {/* Modal de Recordatorios */}
      {showQuickReminder && (
        <QuickReminder
          objetoId={objeto.id || objeto.codigoRegistro}
          objetoNombre={nombre}
          objetoTipo={tipo}
          objetoEspecie={objeto.especie || ''}
          objetoPlan={objeto.plan || 'light'}
          userId={objeto.ownerId || objeto.correo}
          onClose={() => setShowQuickReminder(false)}
          onSuccess={() => setShowQuickReminder(false)}
        />
      )}
    </div>
  );
}

export function UserPanelModals({
  showModalDatos,
  showModalAvatar,
  showEditModal,
  showRewardModal,
  showLostItemForm,
  showVetList,
  showProvidersList,
  showLockModal,
  showPublicProfileModal,
  showPublicProfileForm,
  showPublicProfileSuccess,

  setShowModalDatos,
  setShowModalAvatar,
  setShowEditModal,
  setShowRewardModal,
  setShowLostItemForm,
  setShowVetList,
  setShowProvidersList,
  setShowLockModal,
  setShowPublicProfileModal,
  setShowPublicProfileForm,
  setShowPublicProfileSuccess,

  userFormData,
  lostItemData,
  setLostItemData,
  publicProfileData,
  setPublicProfileData,
  objToEdit,
  objToMarkLost,
  setObjToMarkLost,
  veterinarios,
  vetLimites,
  objetos,
  objToLock,
  setObjToLock,

  onSaveUserData,
  onSaveAvatar,
  onSubmitEdicionObjeto,
  onLostItemSubmit,
  onRewardYes,
  onRewardNo,
  onPublicProfileYes,
  onPublicProfileNo,
  onPublicProfileSubmit,
  onCreateVet,
  onUpdateVet,
  onReInviteVet,
  onDeleteVet,
  onConfirmLock,

  usuarioSeguro,
  LOST_ITEM_FIELDS,
  PUBLIC_PROFILE_FIELDS,
  getCurrentUserId,
  avatarsDisponibles,
}) {
  return (
    <>
      {/* Modal de Datos de Usuario */}
      {showModalDatos && (
       <UserDataModal
       usuario={usuarioSeguro}
        onSave={onSaveUserData}
        onClose={() => setShowModalDatos(false)}
      />
      )}

      {/* Modal de Avatar */}
     {showModalAvatar && (
      <AvatarModal
        currentAvatar={usuarioSeguro?.urlAvatar}  // ✅ AGREGAR
        usuario={usuarioSeguro}                    // ✅ AGREGAR
        avatarsDisponibles={avatarsDisponibles}
        onSaveAvatar={onSaveAvatar}                      // ✅ CAMBIAR nombre
        onClose={() => setShowModalAvatar(false)}
      />
    )}

      {/* Modal de Edición de Objeto */}
      {showEditModal && objToEdit && (
        <ObjectEditModal
          objeto={objToEdit}
          usuario={usuarioSeguro}
          onSubmit={onSubmitEdicionObjeto}
          onClose={() => setShowEditModal(false)}
        />
      )}

      {/* Modal de Recompensa */}
{showRewardModal && objToMarkLost && (
  <div 
    className="modal fade show d-block" 
    style={{ 
      backgroundColor: "rgba(0,0,0,0.5)",
      zIndex: 'var(--z-modal)'
    }}
  >
    <div className="modal-dialog modal-dialog-centered">
      <div className="modal-content" 
           style={{borderRadius: 'var(--radius-xl)'}}>
        
        {/* Header con gradiente */}
        <div className="modal-header modal-header-gradient d-flex justify-content-between align-items-center"
             style={{
               padding: 'var(--space-4)',
               borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0'
             }}>
          <h5 className="modal-title mb-0"
              style={{
                color: 'var(--white)',
                fontWeight: 'var(--font-weight-bold)'
              }}>
            <i className="bi bi-gift me-2"></i>Ofrecer Recompensa
          </h5>
          <button 
            type="button" 
            className="btn-close btn-close-white" 
            onClick={() => {
              setShowRewardModal(false);
              setObjToMarkLost(null);
            }}
            aria-label="Cerrar"
          ></button>
        </div>
        
        {/* Body */}
        <div className="modal-body text-center"
             style={{padding: 'var(--space-5)'}}>
          <div className="mb-4">
            <i className="bi bi-gift-fill" 
               style={{
                 fontSize: 'var(--font-size-4xl)', 
                 color: 'var(--feature-primary)'
               }}></i>
          </div>
          
          <h6 className="mb-3"
              style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: 'var(--font-weight-semibold)'
              }}>
            ¿Deseas ofrecer una recompensa?
          </h6>
          
          <p className="text-muted mb-4"
             style={{fontSize: 'var(--font-size-sm)'}}>
            <i className="bi bi-info-circle me-1"></i>
            Ofrecer una recompensa puede aumentar las probabilidades de recuperar a{" "}
            <strong>{getNombreSegunTipo(objToMarkLost)}</strong>
          </p>
          
          <div className="alert alert-info py-2 px-3"
               style={{
                 fontSize: 'var(--font-size-xs)',
                 borderRadius: 'var(--radius-md)'
               }}>
            <i className="bi bi-star-fill me-1"></i>
            La recompensa será visible en el código QR
          </div>
        </div>
        
        {/* Footer */}
        <div className="modal-footer justify-content-center"
             style={{
               padding: 'var(--space-4)',
               gap: 'var(--space-2)',
               display: 'flex'
             }}>
          <button 
            type="button" 
            className="btn btn-feature transition-base"
            onClick={onRewardYes}
            style={{
              fontSize: 'var(--font-size-sm)',
              padding: 'var(--space-2) var(--space-4)'
            }}>
            <i className="bi bi-gift me-1"></i>
            Sí, ofrecer recompensa
          </button>
          
          <button 
            type="button" 
            className="btn btn-secondary transition-base"
            onClick={onRewardNo}
            style={{
              fontSize: 'var(--font-size-sm)',
              padding: 'var(--space-2) var(--space-4)'
            }}>
            <i className="bi bi-x-circle me-1"></i>
            No, solo marcar como perdido
          </button>
        </div>
      </div>
    </div>
  </div>
)}

{/* Modal de formulario de objeto perdido con recompensa */}
{showLostItemForm && objToMarkLost && (
  <div 
    className="modal fade show d-block" 
    style={{ 
      backgroundColor: "rgba(0,0,0,0.5)",
      zIndex: 'var(--z-modal)'
    }}
  >
    <div className="modal-dialog modal-dialog-centered modal-lg">
      <div className="modal-content" 
           style={{borderRadius: 'var(--radius-xl)'}}>
        
        {/* Header con gradiente verde limón */}
        <div className="modal-header d-flex justify-content-between align-items-center"
             style={{
               padding: 'var(--space-4)',
               borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0',
               background: 'linear-gradient(135deg, #84cc16 0%, #65a30d 100%)'
             }}>
          <h5 className="modal-title mb-0"
              style={{
                color: '#ffffff',
                fontWeight: 'var(--font-weight-bold)'
              }}>
            <i className="bi bi-exclamation-triangle me-2"></i>
            Marcar como Perdido - {getNombreSegunTipo(objToMarkLost)}
          </h5>
          <button 
            type="button" 
            className="btn-close btn-close-white" 
            onClick={() => {
              setShowLostItemForm(false);
              setObjToMarkLost(null);
              setLostItemData({});
            }}
            aria-label="Cerrar"
          ></button>
        </div>
        
        {/* Body con el formulario */}
        <div className="modal-body"
             style={{padding: 'var(--space-4)'}}>
          <div style={{
                 backgroundColor: '#f5f5f5',
                 border: '1px solid #e5e5e5',
                 borderRadius: 'var(--radius-md)',
                 fontSize: 'var(--font-size-sm)',
                 padding: 'var(--space-2) var(--space-3)',
                 marginBottom: 'var(--space-4)',
                 color: '#0a0a0a',
               }}>
            <i className="bi bi-info-circle me-2" style={{ color: '#6b6b6b' }}></i>
            <strong>Importante:</strong> Esta información será visible para quien escanee el código QR
          </div>

          <DynamicForm
            fields={LOST_ITEM_FIELDS}
            recordData={lostItemData}
            onChange={(fieldKey, val) =>
              setLostItemData((prev) => ({ ...prev, [fieldKey]: val }))
            }
            onSubmit={() => {
              // Validar que haya recompensa
              if (!lostItemData.recompensaOfrecida?.trim()) {
                toast.error('Por favor ingresa el monto de la recompensa');
                return;
              }
              onLostItemSubmit(lostItemData);
            }}
            submitButtonText="Marcar como perdido con recompensa"
            submitButtonIcon="bi-exclamation-triangle"
            submitButtonVariant="success"
          />
        </div>
        
        {/* Footer con info adicional */}
        <div className="modal-footer bg-light justify-content-between align-items-center"
             style={{padding: 'var(--space-3)'}}>
          <small className="text-muted" style={{fontSize: 'var(--font-size-xs)'}}>
            <i className="bi bi-info-circle me-1"></i>
            La recompensa será visible para quien escanee el código QR
          </small>
          <button 
            type="button" 
            className="btn btn-sm btn-outline-secondary transition-base"
            onClick={() => {
              setShowLostItemForm(false);
              setObjToMarkLost(null);
              setLostItemData({});
            }}
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  </div>
)}


{/* Modal de Bloqueo/Desbloqueo */}
{showLockModal && objToLock && (
  <div 
    className="modal fade show d-block" 
    style={{ 
      backgroundColor: "rgba(0,0,0,0.5)",
      zIndex: 'var(--z-modal)'
    }}
  >
    <div className="modal-dialog modal-dialog-centered">
      <div className="modal-content" 
           style={{borderRadius: 'var(--radius-xl)'}}>
        
        {/* Header con gradiente Slate */}
        <div className="modal-header modal-header-gradient d-flex justify-content-between align-items-center"
             style={{
               padding: 'var(--space-4)',
               borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0'
             }}>
          <h5 className="modal-title mb-0"
              style={{
                color: 'var(--white)',
                fontWeight: 'var(--font-weight-bold)'
              }}>
            <i className={`bi ${objToLock.bloqueado ? 'bi-unlock' : 'bi-lock'} me-2`}></i>
            {objToLock.bloqueado ? 'Desbloquear' : 'Bloquear'} Objeto
          </h5>
          <button 
            type="button" 
            className="btn-close btn-close-white" 
            onClick={() => {
              setShowLockModal(false);
              setObjToLock(null);
            }}
            aria-label="Cerrar"
          ></button>
        </div>
        
        {/* Body */}
        <div className="modal-body text-center"
             style={{padding: 'var(--space-5)'}}>
          <div className="mb-4">
            <i className={`bi ${objToLock.bloqueado ? 'bi-unlock-fill' : 'bi-lock-fill'}`}
               style={{
                 fontSize: 'var(--font-size-4xl)', 
                 color: objToLock.bloqueado ? 'var(--color-success)' : 'var(--feature-primary)'
               }}></i>
          </div>
          
          <h6 className="mb-3"
              style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: 'var(--font-weight-semibold)'
              }}>
            {objToLock.bloqueado 
              ? '¿Deseas desbloquear este objeto?' 
              : '¿Deseas bloquear este objeto?'}
          </h6>
          
          <p className="text-muted mb-4"
             style={{fontSize: 'var(--font-size-sm)'}}>
            {objToLock.bloqueado ? (
              <>
                <i className="bi bi-info-circle me-1"></i>
                Al desbloquear, cualquier persona con acceso al QR podrá modificar la información del objeto.
              </>
            ) : (
              <>
                <i className="bi bi-shield-check me-1"></i>
                Al bloquear, nadie podrá modificar la información excepto tú desde este panel.
              </>
            )}
          </p>
          
          <div className="alert alert-warning py-2 px-3"
               style={{
                 fontSize: 'var(--font-size-xs)',
                 borderRadius: 'var(--radius-md)'
               }}>
            <i className="bi bi-exclamation-triangle me-1"></i>
            Objeto: <strong>{getNombreSegunTipo(objToLock)}</strong>
          </div>
        </div>
        
        {/* Footer */}
        <div className="modal-footer justify-content-center"
             style={{
               padding: 'var(--space-4)',
               gap: 'var(--space-2)',
               display: 'flex'
             }}>
          <button 
            type="button" 
            className={`btn ${objToLock.bloqueado ? 'btn-success' : 'btn-feature'} transition-base`}
            onClick={onConfirmLock}
            style={{
              fontSize: 'var(--font-size-sm)',
              padding: 'var(--space-2) var(--space-4)'
            }}>
            <i className={`bi ${objToLock.bloqueado ? 'bi-unlock' : 'bi-lock'} me-1`}></i>
            {objToLock.bloqueado ? 'Sí, desbloquear' : 'Sí, bloquear'}
          </button>
          
          <button 
            type="button" 
            className="btn btn-secondary transition-base"
            onClick={() => {
              setShowLockModal(false);
              setObjToLock(null);
            }}
            style={{
              fontSize: 'var(--font-size-sm)',
              padding: 'var(--space-2) var(--space-4)'
            }}>
            <i className="bi bi-x-circle me-1"></i>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  </div>
)}
{/* Modal de Perfil Público - Pregunta */}
{showPublicProfileModal && objToMarkLost && (
  <div
    className="modal fade show d-block"
    style={{
      backgroundColor: "rgba(0,0,0,0.5)",
      zIndex: 'var(--z-modal)'
    }}
  >
    <div className="modal-dialog modal-dialog-centered">
      <div className="modal-content"
           style={{borderRadius: 'var(--radius-xl)'}}>

        {/* Header con gradiente (mismo estilo que modal de recompensa) */}
        <div className="modal-header modal-header-gradient d-flex justify-content-between align-items-center"
             style={{
               padding: 'var(--space-4)',
               borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0'
             }}>
          <h5 className="modal-title mb-0"
              style={{
                color: 'var(--white)',
                fontWeight: 'var(--font-weight-bold)'
              }}>
            <i className="bi bi-globe me-2"></i>Perfil de Búsqueda
          </h5>
          <button
            type="button"
            className="btn-close btn-close-white"
            onClick={() => {
              setShowPublicProfileModal(false);
            }}
            aria-label="Cerrar"
          ></button>
        </div>

        {/* Body */}
        <div className="modal-body text-center"
             style={{padding: 'var(--space-5)'}}>
          <div className="mb-4">
            <i className="bi bi-globe2"
               style={{
                 fontSize: 'var(--font-size-4xl)',
                 color: 'var(--feature-primary)'
               }}></i>
          </div>

          <h6 className="mb-3"
              style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: 'var(--font-weight-semibold)'
              }}>
            ¿Activar perfil público?
          </h6>

          <p className="text-muted mb-3"
             style={{fontSize: 'var(--font-size-sm)'}}>
            <i className="bi bi-info-circle me-1"></i>
            Esto te permite compartir en redes sociales y generar un cartel para imprimir.
          </p>

          <div className="alert alert-info py-2 px-3 text-start"
               style={{
                 fontSize: 'var(--font-size-xs)',
                 borderRadius: 'var(--radius-md)'
               }}>
            <i className="bi bi-shield-check me-1"></i>
            Si solo quieres marcar como perdido, el tag ya permite que quien lo encuentre te contacte.
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer justify-content-center"
             style={{
               padding: 'var(--space-4)',
               gap: 'var(--space-2)',
               display: 'flex'
             }}>
          <button
            type="button"
            className="btn btn-feature transition-base"
            onClick={onPublicProfileYes}
            style={{
              fontSize: 'var(--font-size-sm)',
              padding: 'var(--space-2) var(--space-4)'
            }}>
            <i className="bi bi-globe me-1"></i>
            Sí, activar perfil público
          </button>

          <button
            type="button"
            className="btn btn-secondary transition-base"
            onClick={onPublicProfileNo}
            style={{
              fontSize: 'var(--font-size-sm)',
              padding: 'var(--space-2) var(--space-4)'
            }}>
            <i className="bi bi-x-circle me-1"></i>
            No, solo marcar como perdido
          </button>
        </div>
      </div>
    </div>
  </div>
)}

{/* Modal de Perfil Público - Formulario de campos */}
{showPublicProfileForm && objToMarkLost && (
  <div
    className="modal fade show d-block"
    style={{
      backgroundColor: "rgba(0,0,0,0.5)",
      zIndex: 'var(--z-modal)'
    }}
  >
    <div className="modal-dialog modal-dialog-centered modal-lg">
      <div className="modal-content"
           style={{borderRadius: 'var(--radius-xl)'}}>

        {/* Header con gradiente (mismo estilo que formulario de recompensa) */}
        <div className="modal-header modal-header-gradient d-flex justify-content-between align-items-center"
             style={{
               padding: 'var(--space-4)',
               borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0'
             }}>
          <h5 className="modal-title mb-0"
              style={{
                color: 'var(--white)',
                fontWeight: 'var(--font-weight-bold)'
              }}>
            <i className="bi bi-card-checklist me-2"></i>
            Datos de búsqueda - {getNombreSegunTipo(objToMarkLost)}
          </h5>
          <button
            type="button"
            className="btn-close btn-close-white"
            onClick={() => {
              setShowPublicProfileForm(false);
              setPublicProfileData({});
            }}
            aria-label="Cerrar"
          ></button>
        </div>

        {/* Body */}
        <div className="modal-body"
             style={{padding: 'var(--space-4)'}}>
          <div style={{
                 backgroundColor: '#f5f5f5',
                 border: '1px solid #e5e5e5',
                 borderRadius: 'var(--radius-md)',
                 fontSize: 'var(--font-size-sm)',
                 padding: 'var(--space-2) var(--space-3)',
                 marginBottom: 'var(--space-4)',
                 color: '#0a0a0a',
               }}>
            <i className="bi bi-lightbulb me-2" style={{ color: '#6b6b6b' }}></i>
            <strong>Estos datos ayudan a encontrarlo.</strong> Serán visibles en el perfil público de búsqueda.
          </div>

          <DynamicForm
            fields={PUBLIC_PROFILE_FIELDS}
            recordData={publicProfileData}
            onChange={(fieldKey, val) =>
              setPublicProfileData((prev) => ({ ...prev, [fieldKey]: val }))
            }
            onSubmit={() => {
              onPublicProfileSubmit(publicProfileData);
            }}
            submitButtonText="Activar perfil público"
            submitButtonIcon="bi-globe"
            submitButtonVariant="success"
          />
        </div>

        {/* Footer */}
        <div className="modal-footer bg-light justify-content-between align-items-center"
             style={{padding: 'var(--space-3)'}}>
          <small className="text-muted" style={{fontSize: 'var(--font-size-xs)'}}>
            <i className="bi bi-info-circle me-1"></i>
            Podrás desactivar el perfil público en cualquier momento
          </small>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary transition-base"
            onClick={() => {
              setShowPublicProfileForm(false);
              setPublicProfileData({});
            }}
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  </div>
)}

{/* Modal de Perfil Público - Éxito con acciones de compartir */}
{showPublicProfileSuccess && objToMarkLost && (
  <div
    className="modal fade show d-block"
    style={{
      backgroundColor: "rgba(0,0,0,0.5)",
      zIndex: 'var(--z-modal)'
    }}
  >
    <div className="modal-dialog modal-dialog-centered">
      <div className="modal-content"
           style={{borderRadius: 'var(--radius-xl)'}}>

        {/* Header con gradiente success (turquesa Lemmon) */}
        <div className="modal-header d-flex justify-content-between align-items-center"
             style={{
               padding: 'var(--space-4)',
               borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0',
               background: 'linear-gradient(135deg, var(--color-success) 0%, var(--color-success-dark) 100%)'
             }}>
          <h5 className="modal-title mb-0"
              style={{
                color: 'var(--white)',
                fontWeight: 'var(--font-weight-bold)'
              }}>
            <i className="bi bi-check-circle me-2"></i>Perfil público activo
          </h5>
          <button
            type="button"
            className="btn-close btn-close-white"
            onClick={() => {
              setShowPublicProfileSuccess(false);
              setObjToMarkLost(null);
            }}
            aria-label="Cerrar"
          ></button>
        </div>

        {/* Body */}
        <div className="modal-body text-center"
             style={{padding: 'var(--space-5)'}}>
          <div className="mb-3">
            <i className="bi bi-check-circle-fill"
               style={{ fontSize: 'var(--font-size-4xl)', color: 'var(--color-success)' }}></i>
          </div>
          <h6 className="mb-2"
              style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: 'var(--font-weight-semibold)'
              }}>
            {getNombreSegunTipo(objToMarkLost)} ya tiene perfil público
          </h6>
          <p className="text-muted mb-4" style={{fontSize: 'var(--font-size-sm)'}}>
            Comparte el link en redes o imprime el cartel para aumentar las probabilidades de encontrarlo.
          </p>

          {/* Link público */}
          <div className="mb-3 p-2 rounded"
               style={{
                 fontSize: 'var(--font-size-xs)',
                 wordBreak: 'break-all',
                 border: '1px solid var(--gray-200)',
                 backgroundColor: 'var(--gray-50)',
                 color: 'var(--gray-700)'
               }}>
            {`${window.location.origin}/perdido/${objToMarkLost.id}`}
          </div>

          {/* Botones de compartir */}
          <div className="d-flex flex-column" style={{gap: 'var(--space-2)'}}>
            <div className="d-flex" style={{gap: 'var(--space-2)'}}>
              <button
                className="btn flex-fill transition-base"
                style={{
                  backgroundColor: '#25D366', color: 'var(--white)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semibold)',
                  border: 'none'
                }}
                onClick={() => {
                  const nombre = getNombreSegunTipo(objToMarkLost);
                  const url = `${window.location.origin}/perdido/${objToMarkLost.id}`;
                  const texto = `Ayúdame a encontrar a ${nombre}. Mira su perfil de búsqueda: ${url}`;
                  window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, '_blank');
                }}
              >
                <i className="bi bi-whatsapp me-1"></i>WhatsApp
              </button>
              <button
                className="btn flex-fill transition-base"
                style={{
                  backgroundColor: '#1877F2', color: 'var(--white)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semibold)',
                  border: 'none'
                }}
                onClick={() => {
                  const url = `${window.location.origin}/perdido/${objToMarkLost.id}`;
                  window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
                }}
              >
                <i className="bi bi-facebook me-1"></i>Facebook
              </button>
            </div>
            <div className="d-flex" style={{gap: 'var(--space-2)'}}>
              <button
                className="btn btn-outline-secondary flex-fill transition-base"
                style={{ fontSize: 'var(--font-size-sm)' }}
                onClick={() => {
                  const url = `${window.location.origin}/perdido/${objToMarkLost.id}`;
                  navigator.clipboard.writeText(url);
                  toast.info("Link copiado al portapapeles");
                }}
              >
                <i className="bi bi-link-45deg me-1"></i>Copiar link
              </button>
              <button
                className="btn btn-outline-secondary flex-fill transition-base"
                style={{ fontSize: 'var(--font-size-sm)' }}
                onClick={() => {
                  const url = `${window.location.origin}/perdido/${objToMarkLost.id}`;
                  window.open(url, '_blank');
                }}
              >
                <i className="bi bi-printer me-1"></i>Imprimir cartel
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer justify-content-center"
             style={{ padding: 'var(--space-3)' }}>
          <button
            type="button"
            className="btn btn-outline-secondary transition-base"
            style={{ fontSize: 'var(--font-size-sm)' }}
            onClick={() => {
              setShowPublicProfileSuccess(false);
              setObjToMarkLost(null);
            }}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  </div>
)}

      {/* Modal Veterinarios */}
      {showVetList && (
        <ModalOverlay
          onClose={() => setShowVetList(false)}
          containerStyle={{
            padding: "var(--space-4)",
            border: `1px solid var(--gray-300)`,
            borderRadius: "var(--radius-lg)",
            width: "90%",
            maxWidth: "600px",
          }}
        >
          <ExpedienteClinicoVeterinarioList
            veterinarios={veterinarios}
            vetLimites={vetLimites}
            onUpdateVet={onUpdateVet}
            onCreateVet={onCreateVet}
            onReInviteVet={onReInviteVet}
            onDeleteVet={onDeleteVet}
            onClose={() => setShowVetList(false)}
          />
        </ModalOverlay>
      )}

      {/* Modal Proveedores */}
      {showProvidersList && (
        <ModalOverlay 
          onClose={() => setShowProvidersList(false)}
          containerStyle={{
            padding: "0",
            width: "90%",
            maxWidth: "800px",
            borderRadius: "var(--radius-xl)"
          }}
        >
          <ProvidersList 
            ownerId={getCurrentUserId()}   
            onClose={() => setShowProvidersList(false)}
          />
        </ModalOverlay>
      )}
    </>
  );
}